import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import {
  useGetChequebookQuery,
  useGetChequebookLeavesQuery,
} from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ChequebookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const { data: book, isLoading: bookLoading } = useGetChequebookQuery(id);
  const { data: leavesData, isLoading: leavesLoading } = useGetChequebookLeavesQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const chequebook = leavesData?.chequebook || book;
  const leaves = leavesData?.leaves || [];

  const formatAmount = (v) => {
    const n = Number(v || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(n);
    } catch {
      return `${currency} ${n.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '–');

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head><title>Chequebook ${chequebook?.name || id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; }
          .header { margin-bottom: 16px; }
        </style>
        </head>
        <body>
          <div class="header">
            <h2>${company?.name || 'Company'} – Chequebook: ${chequebook?.name || ''}</h2>
            <p>Bank: ${chequebook?.bankAccount?.name || ''} ${chequebook?.bankAccount?.accountNumber ? `(${chequebook.bankAccount.accountNumber})` : ''}</p>
            <p>Leaves ${chequebook?.startLeaf ?? ''} to ${chequebook?.endLeaf ?? ''}</p>
            <p><strong>Given date (printed on):</strong> ${new Date().toLocaleDateString(undefined, { dateStyle: 'long' })}</p>
          </div>
          <table>
            <thead><tr><th>Leaf #</th><th>Status</th><th>Payee</th><th>Amount</th><th>Given date</th></tr></thead>
            <tbody>
              ${(leaves || []).map((l) => `
                <tr>
                  <td>${l.leafNumber}</td>
                  <td>${l.status}</td>
                  <td>${l.cheque?.payee || '–'}</td>
                  <td>${l.cheque?.amount != null ? formatAmount(l.cheque.amount) : '–'}</td>
                  <td>${l.cheque?.chequeDate ? formatDate(l.cheque.chequeDate) : '–'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
  };

  if (bookLoading && !chequebook) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance/chequebooks')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          {chequebook?.name || 'Chequebook'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/finance/chequebooks/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/finance/cheques/new?chequebookId=${id}`)}
          >
            Issue cheque
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Bank: {chequebook?.bankAccount?.name || '–'}
            {chequebook?.bankAccount?.accountNumber && ` (${chequebook.bankAccount.accountNumber})`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Leaves {chequebook?.startLeaf ?? ''} – {chequebook?.endLeaf ?? ''} · Status: {chequebook?.status === 'active' ? 'Active' : 'Closed'}
          </Typography>
        </CardContent>
      </Card>

      <Card ref={printRef}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Leaves
          </Typography>
          {leavesLoading && !leaves.length ? (
            <Typography color="text.secondary">Loading leaves...</Typography>
          ) : leaves.length === 0 ? (
            <Typography color="text.secondary">No leaves.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Leaf #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Given date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaves.map((leaf) => (
                    <TableRow key={leaf.leafNumber} hover>
                      <TableCell>{leaf.leafNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={leaf.status}
                          size="small"
                          color={leaf.status === 'used' ? 'primary' : leaf.status === 'cancelled' ? 'default' : 'success'}
                        />
                      </TableCell>
                      <TableCell>{leaf.cheque?.payee || '–'}</TableCell>
                      <TableCell align="right">
                        {leaf.cheque?.amount != null ? formatAmount(leaf.cheque.amount) : '–'}
                      </TableCell>
                      <TableCell>{leaf.cheque?.chequeDate ? formatDate(leaf.cheque.chequeDate) : '–'}</TableCell>
                      <TableCell>
                        {leaf.cheque?.id && (
                          <Button size="small" onClick={() => navigate(`/finance/cheques/${leaf.cheque.id}`)}>
                            View
                          </Button>
                        )}
                        {leaf.status === 'available' && (
                          <Button
                            size="small"
                            onClick={() => navigate(`/finance/cheques/new?chequebookId=${id}&leaf=${leaf.leafNumber}`)}
                          >
                            Issue
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChequebookDetail;
